import { useCallback, useRef } from 'react';
import { StaffCacheService } from '@rsmaids/shared/services/api/staffCacheService';
import { sendStaffMessage } from '@/services/staffMessageService';

interface SentMessageCache {
  text: string;
  staffName: string;
  staffId: string;
  contactId: string;
  avatarUrl: string;
}

interface Message {
  id?: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean;
  avatarUrl?: string;
  extractedStaffInfo?: { staffName: string; avatarUrl?: string };
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  media?: Array<{
    url?: string;
    type: 'image' | 'video' | 'file' | 'icon';
    name?: string;
    iconName?: string;
  }>;
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
}

interface SendMessageOptions {
  id: string | undefined;
  message: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  user?: {
    id: string;
    name?: string;
    email?: string;
    wixContactId?: string;
    avatarUrl?: string;
    profileData?: Record<string, unknown>;
  };
  displayName: string;
  contactName?: string;
  contactAvatarUrl?: string;
  targetChannelId?: string;
  subject?: string;
  fromEmail?: 'scheduling' | 'billing' | 'support';
  /** The app's own id for this send (see sendStaffMessage) */
  clientMessageId?: string;
  /** Files already uploaded to Wix Media, sent with an email */
  attachments?: Array<{ id: string; url: string; name: string; mimeType: string; size: number }>;
  onMessageSent: (optimisticMessage: Message) => void;
  onClearInput: () => void;
  /** Called when the message could not be sent */
  onError?: () => void;
}

export const useSendMessage = () => {
  const sentMessagesRef = useRef<SentMessageCache[]>([]);

  const handleSend = useCallback(async (options: SendMessageOptions) => {
    const { id, message, channel, user, onMessageSent, onClearInput } = options;

    if (!message.trim() || !['chat', 'email', 'sms', 'gmail', 'outlook', 'yahoo', 'other-email'].includes(channel) || !id) {
      console.warn(`📤 Cannot send: message="${!!message.trim()}" channel="${channel}" id="${id}"`);
      return;
    }

    try {
      const sendAsContactId = user?.wixContactId;

      if (!sendAsContactId) {
        console.error(`❌ User does not have wixContactId`);
        console.error(`   - User: ${user?.name} (${user?.email})`);
        console.error(`   - User object:`, { id: user?.id, wixContactId: user?.wixContactId, profileData: user?.profileData });
        return;
      }

      console.log(`✅ Sending message as staff with contactId: ${sendAsContactId}`);

      let staffName = user?.name || 'Unknown';
      let staffId = user?.id || '';
      let avatarUrl = user?.avatarUrl || '';

      console.log(`🖼️ Current user avatar URL:`, avatarUrl);

      try {
        const staffCache = StaffCacheService.getInstance();
        await staffCache.loadStaffCache();
        const allStaff = staffCache.getAllStaff();
        const userStaff = allStaff.find(s => s.contactId === sendAsContactId);

        if (userStaff) {
          staffName = userStaff.name;
          staffId = userStaff.staffMemberId;
          console.log(`👤 Found staff info: ${staffName} (${staffId})`);
        }
      } catch (cacheErr) {
        console.warn(`⚠️ Could not load staff cache, using user info:`, cacheErr);
      }

      const channelMap: Record<'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email', 'CHAT' | 'GMAIL' | 'OUTLOOK' | 'YAHOO' | 'OTHER_EMAIL' | 'SMS'> = {
        'chat': 'CHAT',
        'email': 'OTHER_EMAIL',
        'gmail': 'GMAIL',
        'outlook': 'OUTLOOK',
        'yahoo': 'YAHOO',
        'other-email': 'OTHER_EMAIL',
        'sms': 'SMS'
      };
      const wixChannel = channelMap[channel];

      console.log(`📤 Sending ${wixChannel} message from staff contact ${sendAsContactId} in conversation ${id}`);
      console.log(`👤 Staff info embedded: staffName="${staffName}", staffId="${staffId}", contactId="${sendAsContactId}", avatarUrl="${avatarUrl}"`);

      // Use staffMessage handler for proper metadata storage
      const result = await sendStaffMessage(
        id,
        message,
        staffId,
        staffName,
        wixChannel,
        options.contactName,
        options.contactAvatarUrl,
        options.targetChannelId,
        options.subject,
        options.fromEmail,
        options.clientMessageId,
        options.attachments
      );

      if (result) {
        console.log(`✅ Message sent successfully`);

        sentMessagesRef.current.push({
          text: message,
          staffName,
          staffId,
          contactId: sendAsContactId,
          avatarUrl
        });
        console.log(`💾 Cached sent message with staff info for retrieval`);

        if (sentMessagesRef.current.length > 50) {
          sentMessagesRef.current.shift();
        }

        onClearInput();
        localStorage.setItem('lastUsedChannel', channel);

        const optimisticMessage: Message = {
          from: staffName,
          text: message,
          channel: channel,
          _createdDate: new Date(),
          isFromCustomer: false,
          avatarUrl: avatarUrl || '/assets/icon/maskable_icon_192.png',
          senderStaffName: staffName,
          senderStaffAvatar: avatarUrl,
          senderStaffId: staffId,
          senderType: 'staff'
        };

        onMessageSent(optimisticMessage);
        console.log(`✅ Optimistically added message to UI - real-time listener will replace with canonical version`);
      } else {
        console.error(`❌ Failed to send message`);
        options.onError?.();
      }
    } catch (error) {
      console.error(`❌ Error sending message:`, error);
      options.onError?.();
    }
  }, []);

  return { handleSend, sentMessagesRef };
};
